/**
 * The two drop targets ACTION_PLAN.md's E4 §1 describes — source and
 * destination each get one `SchemaDropBox`, GenerateJob.tsx renders the
 * pair.
 *
 * A recognised file extension pre-selects the schema format
 * (parsers/registry.py's five schema-native v1 formats); an unrecognised
 * one still accepts and reads the file — the upload isn't hard-blocked,
 * since a format the extension can't guess may still be one the backend
 * supports — it just flags the file and leaves the format select
 * unset for the caller to pick by hand.
 *
 * E5 adds six sample-data inference formats (Flow B) to the format
 * picker below, but deliberately gains no new extension auto-guesses
 * for them beyond `.csv`/`.xml` (both new, no prior conflict): `.json`/
 * `.yaml`/`.yml` already guess a schema-native format (json_schema/
 * openapi) from E4, and most schema uploads are still schema-native —
 * changing those guesses would silently change E4's established Flow A
 * default. `.xml` had no such conflict (the schema-native XML format
 * uses the separate `.xsd` extension), so leaving it unguessed was just
 * an oversight, not a deliberate ambiguity call like the other three —
 * confirmed live post-deploy: every real .xml drop showed "Unrecognised"
 * for no reason. A sample-data upload with a `.json`/`.yaml`/`.yml`
 * extension still works exactly like any other "unrecognised" one
 * already does: unrecognized=false doesn't apply, but the format select
 * is always shown regardless, so the caller just picks the sample-data
 * format by hand.
 *
 * A content textarea sits below the drop target, always rendered, bound
 * directly to `value.content` — edits a dropped file's content in place
 * (name/format survive the edit) and, with no file dropped at all, is a
 * second way into the same state a drop would have produced: typing
 * starts a schema from scratch, picking its own format by hand from the
 * select below (there's no filename to guess one from) — UNLESS content
 * sniffing resolves it first (below).
 *
 * Content sniffing, added once the textarea above made the old `.json`
 * extension-only guess a real live problem, not just a documented
 * tradeoff: typed/pasted content has no extension to guess from at all,
 * and a `.json` *file* was already ambiguous between json_schema and
 * sample-data json/mongo (this module's own prior paragraph's "most
 * schema uploads are still schema-native" reasoning for defaulting
 * `.json` to json_schema regardless). `guessFormatFromContent` resolves
 * that ambiguity directly — valid JSON that looks like a JSON Schema
 * document (`$schema`, or `type: "object"` + a `properties` object,
 * matching `json_schema.py`'s own parse contract exactly) guesses
 * `json_schema`; any other valid JSON (an array, or an object without
 * those keys) guesses `json` (sample data, `json_infer.py`'s own
 * contract: a single object or an array of objects). Content with a
 * line starting `CREATE TABLE` is DDL: `sqlserver_ddl` when it uses
 * anything only T-SQL writes (`SQLSERVER_MARKERS`), `postgres_ddl`
 * otherwise; a `.sql` file gets the same dialect check, since its
 * extension says DDL but not which. Only ever
 * guessed once — the moment `value.format` is non-empty (an earlier
 * guess, or a manual pick), further edits never overwrite it, same as
 * a dropped file's own extension-guessed format already survives edits.
 * Deliberately NOT extended to `.yaml`/`.yml`/`.xml`/etc: those aren't
 * JSON-parseable, so there's no equally cheap, reliable sniff for them
 * — still exactly the "pick by hand" default this module's own earlier
 * paragraphs already describe for every other ambiguous case.
 */

import { useState, type ChangeEvent, type DragEvent } from 'react';

export interface DroppedSchema {
  name: string;
  content: string;
  /** '' until a format is known — either guessed from the extension or
   *  picked by hand. */
  format: string;
}

const EXTENSION_FORMAT: Record<string, string> = {
  xsd: 'xsd',
  json: 'json_schema',
  yaml: 'openapi',
  yml: 'openapi',
  csv: 'csv',
  xml: 'xml',
};

/** parsers/registry.py's `_PARSERS` keys: E3's five schema-native
 *  formats (Flow A) plus E5's six sample-data inference formats plus
 *  E13's two legacy-bridge sample-data formats (Flow B). E13's own
 *  entries get no extension auto-guess either, same "pick the format
 *  by hand" default `mongo`/`other` already use (design spec §5
 *  step 5) — a pasted FIX/SWIFT sample has no conventional file
 *  extension to guess from anyway. */
const FORMAT_OPTIONS: { value: string; label: string }[] = [
  { value: 'xsd', label: 'XSD (XML Schema)' },
  { value: 'json_schema', label: 'JSON Schema' },
  { value: 'openapi', label: 'OpenAPI' },
  { value: 'postgres_ddl', label: 'PostgreSQL DDL' },
  { value: 'sqlserver_ddl', label: 'SQL Server DDL' },
  { value: 'csv', label: 'CSV (sample data)' },
  { value: 'json', label: 'JSON (sample data)' },
  { value: 'xml', label: 'XML (sample data)' },
  { value: 'yaml', label: 'YAML (sample data)' },
  { value: 'mongo', label: 'MongoDB documents (sample data)' },
  { value: 'other', label: 'Other / unrecognized (AI-assisted inference)' },
  { value: 'fix', label: 'FIX message (sample data)' },
  { value: 'swift_mt', label: 'SWIFT MT message (sample data)' },
];

/** A line that starts a `CREATE TABLE` statement: DDL. */
const DDL_CREATE_TABLE = /^\s*CREATE\s+TABLE\b/im;

/** What only T-SQL writes, so a document with any of it is SQL Server DDL: a bracketed
 *  identifier (`[dbo].[orders]`; not an array type like `int[3]`), a seeded `IDENTITY(1,1)`
 *  (PostgreSQL's `GENERATED ... AS IDENTITY` has no seed pair), types and functions with no
 *  PostgreSQL counterpart, and the `GO` batch separator. */
const SQLSERVER_MARKERS: RegExp[] = [
  /(?:^|[\s(,.])\[[A-Za-z_][\w ]*\]/,
  /\bIDENTITY\s*\(\s*\d+\s*,\s*\d+\s*\)/i,
  /\b(?:NVARCHAR|DATETIME2|DATETIMEOFFSET|UNIQUEIDENTIFIER|NONCLUSTERED)\b/i,
  /\b(?:GETDATE|GETUTCDATE|SYSDATETIME|NEWID)\s*\(/i,
  /^\s*GO\s*$/im,
];

/** Which DDL dialect `content` is. T-SQL markers (comments removed first, so a `[TODO]` in
 *  one isn't taken for a bracketed identifier) mean sqlserver_ddl; DDL without any is
 *  postgres_ddl, the default for the generic `CREATE TABLE` most exports use. */
function guessDdlFormat(content: string): 'sqlserver_ddl' | 'postgres_ddl' {
  const code = content.replace(/--[^\n]*|\/\*[\s\S]*?\*\//g, '');
  return SQLSERVER_MARKERS.some((marker) => marker.test(code)) ? 'sqlserver_ddl' : 'postgres_ddl';
}

/** Sniffs valid JSON content to resolve the json_schema/json ambiguity
 *  — see this module's own docstring — and DDL by its `CREATE TABLE`
 *  (which dialect: guessDdlFormat). Returns null for anything else
 *  (XML/CSV/FIX/SWIFT/YAML content, or a file mid-edit that doesn't
 *  parse yet), leaving those to extension guessing or a manual pick
 *  exactly as before. */
function guessFormatFromContent(content: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch {
    return DDL_CREATE_TABLE.test(content) ? guessDdlFormat(content) : null;
  }
  if (Array.isArray(parsed)) return 'json'; // json_schema.py requires a top-level object
  if (parsed === null || typeof parsed !== 'object') return null; // a bare scalar — no format here parses that
  const obj = parsed as Record<string, unknown>;
  const looksLikeJsonSchema =
    typeof obj.$schema === 'string' ||
    (obj.type === 'object' && typeof obj.properties === 'object' && obj.properties !== null);
  return looksLikeJsonSchema ? 'json_schema' : 'json';
}

function guessFormat(filename: string, content: string): string | null {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'json') return guessFormatFromContent(content) ?? EXTENSION_FORMAT.json ?? null;
  if (ext === 'sql') return guessDdlFormat(content); // the extension says DDL; the content, which dialect
  return EXTENSION_FORMAT[ext] ?? guessFormatFromContent(content);
}

interface SchemaDropBoxProps {
  label: string;
  value: DroppedSchema | null;
  onChange: (schema: DroppedSchema | null) => void;
}

export function SchemaDropBox({ label, value, onChange }: SchemaDropBoxProps) {
  const [dragOver, setDragOver] = useState(false);
  const [unrecognized, setUnrecognized] = useState(false);

  async function acceptFile(file: File) {
    const content = await file.text();
    const guessed = guessFormat(file.name, content);
    setUnrecognized(guessed === null);
    onChange({ name: file.name, content, format: guessed ?? '' });
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) void acceptFile(file);
  }

  function handleFileInput(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) void acceptFile(file);
  }

  function handleClear() {
    setUnrecognized(false);
    onChange(null);
  }

  function handleContentEdit(event: ChangeEvent<HTMLTextAreaElement>) {
    // Edits the dropped file's content in place when one exists (format/
    // name stay put once set, so neither a dropped file's extension-
    // guessed format nor a manual pick is ever overwritten by further
    // edits); starts a brand-new schema, no file needed, when there
    // isn't one yet — "pasted" is a display-only placeholder name,
    // never read for its own sake. While no format is set yet, content
    // sniffing gets a fresh attempt on every edit (cheap — JSON.parse
    // on a string that mostly isn't valid JSON until typing completes
    // a real document), so a typed/pasted schema's format can resolve
    // itself the moment it becomes recognisable, the same as dropping
    // a recognised file already does immediately.
    const content = event.target.value;
    const format = value?.format || guessFormatFromContent(content) || '';
    onChange({ name: value?.name ?? 'pasted', format, content });
  }

  return (
    <div
      className={`drop-box${dragOver ? ' drop-box--over' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <div className="drop-box-header">
        <p className="drop-box-label">{label}</p>
        {value && (
          <button type="button" className="drop-box-clear" onClick={handleClear}>
            Clear
          </button>
        )}
      </div>
      <label className="drop-box-input">
        {value ? value.name : 'Drag a file here, or click to browse'}
        <input type="file" onChange={handleFileInput} hidden />
      </label>

      <textarea
        className="drop-box-editor"
        value={value?.content ?? ''}
        onChange={handleContentEdit}
        placeholder="…or type/paste the content directly"
        spellCheck={false}
        aria-label={`${label} content`}
      />

      {value && unrecognized && (
        <p className="drop-box-warning" role="alert">
          Unrecognised file extension — pick the format below.
        </p>
      )}

      {value && (
        <label className="drop-box-format">
          <span>Format</span>
          <select
            value={value.format}
            onChange={(event) => onChange({ ...value, format: event.target.value })}
          >
            <option value="" disabled>
              Select a format…
            </option>
            {FORMAT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}
